import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Papa from "papaparse";
import { toast } from "sonner";
import {
  Filter,
  CalendarDays,
  Download,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  XCircle,
} from "lucide-react";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InfoTooltip } from "@/components/ui/tooltip";
import { getErrorMessage } from "@/lib/api/client";
import { getAllUserActivity, getUserActivity } from "@/lib/api/stats";
import { listSimulators } from "@/lib/api/simulators";
import { RolesGuard } from "@/lib/auth/RoleGuard";

export const Route = createFileRoute("/_dashboard/admin/user-activity")({
  component: UserActivityPage,
});

const roleLabels: Record<string, { label: string; color: string }> = {
  admin: { label: "Administrateur", color: "bg-error/20 text-error" },
  manager: { label: "Responsable", color: "bg-laposte-navy/20 text-laposte-navy" },
  user: { label: "Utilisateur", color: "bg-info/20 text-info" },
  viewer: { label: "Consultant", color: "bg-muted text-muted-foreground" },
};

function formatDateParis(dateStr: string): string {
  let d: Date;
  if (!dateStr.endsWith("Z") && !dateStr.includes("+")) {
    d = new Date(dateStr + "Z");
  } else {
    d = new Date(dateStr);
  }
  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

type SortKey =
  | "full_name"
  | "simulation_events"
  | "cotations"
  | "proposals"
  | "last_login"
  | "last_activity";

function UserActivityPage() {
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [simulatorFilter, setSimulatorFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [onlyConnected, setOnlyConnected] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>("cotations");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isExporting, setIsExporting] = useState(false);

  const { data: simulatorsData } = useQuery({
    queryKey: ["simulators-list"],
    queryFn: () => listSimulators({ page_size: 100, include_unpublished: true }),
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      "user-activity",
      page,
      pageSize,
      simulatorFilter,
      dateFrom,
      dateTo,
      searchTerm,
      onlyConnected,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      getUserActivity({
        page,
        page_size: pageSize,
        simulator_id: simulatorFilter !== "all" ? parseInt(simulatorFilter) : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        search: searchTerm || undefined,
        only_connected: onlyConnected || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      }),
  });

  const resetFilters = () => {
    setSimulatorFilter("all");
    setDateFrom("");
    setDateTo("");
    setSearchTerm("");
    setOnlyConnected(false);
    setPage(1);
  };

  const hasActiveFilters =
    simulatorFilter !== "all" ||
    dateFrom !== "" ||
    dateTo !== "" ||
    searchTerm !== "" ||
    onlyConnected;

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortBy !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortOrder === "asc" ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  const exportCsv = async () => {
    setIsExporting(true);
    try {
      // Tous les comptes des filtres en cours, pas seulement la page affichée.
      const users = await getAllUserActivity({
        simulator_id: simulatorFilter !== "all" ? parseInt(simulatorFilter) : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        search: searchTerm || undefined,
        only_connected: onlyConnected || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      const csv = Papa.unparse(
        {
          fields: [
            "Utilisateur",
            "Email",
            "Rôle",
            "Statut",
            "Dernière connexion",
            "Simulations",
            "Cotations",
            "Propositions",
            "Dernière activité",
          ],
          data: users.map((u) => [
            u.full_name,
            u.email,
            roleLabels[u.role]?.label ?? u.role,
            u.is_active ? "Actif" : "Inactif",
            u.last_login ? formatDateParis(u.last_login) : "—",
            u.simulation_events,
            u.cotations,
            u.proposals,
            u.last_activity ? formatDateParis(u.last_activity) : "—",
          ]),
        },
        { delimiter: ";" },
      );
      // Sans BOM, Excel lit le fichier en Windows-1252 et abîme les accents.
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `suivi-utilisateurs-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Export impossible", { description: getErrorMessage(error) });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <RolesGuard allow={["viewer", "manager", "admin"]}>
      <div className="flex flex-col h-full">
        <Header title="Suivi utilisateurs" />

        <div className="flex-1 p-6 space-y-6 overflow-auto">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Filter className="h-4 w-4" />
                  <span className="text-sm font-medium">Filtres</span>
                </div>

                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher un utilisateur…"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(1);
                    }}
                    className="pl-9 h-9 w-[220px]"
                  />
                </div>

                <Select
                  value={simulatorFilter}
                  onValueChange={(v) => {
                    setSimulatorFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Simulateur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les simulateurs</SelectItem>
                    {simulatorsData?.items.map((sim) => (
                      <SelectItem key={sim.id} value={String(sim.id)}>
                        {sim.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="ua-date-from" className="sr-only">Du</Label>
                    <Input
                      id="ua-date-from"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setPage(1);
                      }}
                      className="h-9 w-[140px] text-sm"
                    />
                    <span className="text-xs text-muted-foreground">–</span>
                    <Label htmlFor="ua-date-to" className="sr-only">Au</Label>
                    <Input
                      id="ua-date-to"
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setPage(1);
                      }}
                      className="h-9 w-[140px] text-sm"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="only-connected"
                    checked={onlyConnected}
                    onCheckedChange={(v) => {
                      setOnlyConnected(v);
                      setPage(1);
                    }}
                  />
                  <Label htmlFor="only-connected" className="text-sm cursor-pointer">
                    Connectés uniquement
                  </Label>
                </div>

                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={resetFilters}>
                    Réinitialiser
                  </Button>
                )}

                <div className="ml-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportCsv}
                    disabled={!data?.items.length || isExporting}
                  >
                    <Download className="h-4 w-4 mr-1.5" />
                    CSV
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("full_name")}
                      >
                        <div className="flex items-center">
                          Utilisateur
                          <SortIcon column="full_name" />
                        </div>
                      </TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("last_login")}
                      >
                        <div className="flex items-center">
                          Dernière connexion
                          <SortIcon column="last_login" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none text-right"
                        onClick={() => handleSort("simulation_events")}
                      >
                        <div className="flex items-center justify-end">
                          Simulations
                          <SortIcon column="simulation_events" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none text-right"
                        onClick={() => handleSort("cotations")}
                      >
                        <div className="flex items-center justify-end">
                          Cotations
                          <SortIcon column="cotations" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none text-right"
                        onClick={() => handleSort("proposals")}
                      >
                        <div className="flex items-center justify-end">
                          Propositions
                          <SortIcon column="proposals" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none"
                        onClick={() => handleSort("last_activity")}
                      >
                        <div className="flex items-center">
                          Dernière activité
                          <SortIcon column="last_activity" />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 8 }).map((_, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-5 w-full" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : !data?.items.length ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          Aucun utilisateur trouvé
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.items.map((user) => (
                        <TableRow key={user.user_id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{user.full_name}</div>
                              <div className="text-xs text-muted-foreground">{user.email}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={roleLabels[user.role]?.color ?? "bg-muted"}>
                              {roleLabels[user.role]?.label ?? user.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {user.is_active ? (
                              <Badge variant="default" className="gap-1 bg-success/20 text-success hover:bg-success/30">
                                <CheckCircle2 className="h-3 w-3" />
                                Actif
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="gap-1">
                                <XCircle className="h-3 w-3" />
                                Inactif
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {user.last_login ? (
                              formatDateParis(user.last_login)
                            ) : (
                              <InfoTooltip
                                content="La date de dernière connexion n'est enregistrée que depuis la mise en place du suivi le 07/04/2026."
                                side="top"
                              >
                                <span className="text-muted-foreground cursor-help">—</span>
                              </InfoTooltip>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {user.simulation_events.toLocaleString("fr-FR")}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {user.cotations.toLocaleString("fr-FR")}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {user.proposals.toLocaleString("fr-FR")}
                          </TableCell>
                          <TableCell className="text-sm">
                            {user.last_activity ? (
                              formatDateParis(user.last_activity)
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {data.total_items} utilisateur{data.total_items > 1 ? "s" : ""} au total
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Précédent
                </Button>
                <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded text-sm">
                  {page} / {data.total_pages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                  disabled={page === data.total_pages}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </RolesGuard>
  );
}
